import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { handleError, validateFileUpload } from '../utils/businessValidation';
import { useToast } from '../components/ui/Toast';

export const useMediaManagement = (businessId?: string, onLogoUpdate?: () => void) => {
  const [logo, setLogo] = useState<File | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [newGalleryImages, setNewGalleryImages] = useState<File[]>([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  
  // Video management state
  const [shopVideo, setShopVideo] = useState<File | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoDeleting, setVideoDeleting] = useState(false);
  const [shopVideoUrl, setShopVideoUrl] = useState<string>("");

  const { success: showSuccessToast, error: showErrorToast } = useToast();

  // Initialize logo URL, gallery images, and shop video from business data
  useEffect(() => {
    const fetchBusinessMedia = async () => {
      if (!businessId) {
        setLogoUrl("");
        setGalleryImages([]);
        setShopVideoUrl("");
        return;
      }

      try {
        const { data, error } = await (supabase as any)
          .from('businesses')
          .select('logo_url, gallery, shop_video_url')
          .eq('id', businessId)
          .single();

        if (error) {
          console.warn('Failed to fetch business media:', error);
          setLogoUrl("");
          setGalleryImages([]);
          setShopVideoUrl("");
          return;
        }

        if (data) {
          // Ensure we always set valid types
          setLogoUrl(typeof data.logo_url === 'string' ? data.logo_url : "");
          setGalleryImages(Array.isArray(data.gallery) ? data.gallery : []);
          setShopVideoUrl(typeof data.shop_video_url === 'string' ? data.shop_video_url : "");
        } else {
          setLogoUrl("");
          setGalleryImages([]);
          setShopVideoUrl("");
        }
      } catch (err) {
        console.warn('Error fetching business media:', err);
        setLogoUrl("");
        setGalleryImages([]);
        setShopVideoUrl("");
      }
    };

    fetchBusinessMedia();
  }, [businessId]);

  const uploadLogo = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from("business-logos")
      .upload(`logos/${fileName}`, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      throw new Error(`Logo upload failed: ${error.message}`);
    }
    
    if (!data?.path) {
      throw new Error("Upload succeeded but no path returned");
    }
    
    const { data: { publicUrl } } = supabase.storage
      .from("business-logos")
      .getPublicUrl(data.path);
    
    // Return the public URL, which should be a permanent link
    return publicUrl;
  };

  const validateLogoRequirements = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      // Check file type - only JPG and PNG allowed
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        resolve('Please select a JPG or PNG file only.');
        return;
      }

      // Check file size (1MB = 1024 * 1024 bytes)
      if (file.size > 1024 * 1024) {
        resolve('File size must be under 1MB.');
        return;
      }

      // Use FileReader instead of URL.createObjectURL to avoid CSP issues
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const img = new Image();
          
          img.onload = () => {
            try {
              if (img.width !== 600 || img.height !== 600) {
                resolve(`Logo dimensions are ${img.width}x${img.height}px. Must be exactly 600x600px (square).`);
              } else {
                resolve(null);
              }
            } catch (error) {
              console.error('Error reading image dimensions:', error);
              resolve('Error reading image dimensions. Please try a different image.');
            }
          };
          
          img.onerror = (event) => {
            console.error('Image validation error:', event);
            resolve('Invalid image file. Please select a valid JPG or PNG image.');
          };
          
          // Use data URL from FileReader instead of blob URL
          if (e.target?.result && typeof e.target.result === 'string') {
            img.src = e.target.result;
          } else {
            resolve('Unable to read the selected file. Please try a different image.');
          }
        } catch (error) {
          console.error('Failed to process image:', error);
          resolve('Unable to process the selected file. Please try a different image.');
        }
      };
      
      reader.onerror = () => {
        console.error('FileReader error');
        resolve('Unable to read the selected file. Please try a different image.');
      };
      
      try {
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Failed to read file:', error);
        resolve('Unable to process the selected file. Please try a different image.');
      }
    });
  };

  const handleLogoUpload = async () => {
    if (!logo || !businessId) {
      showErrorToast("Please select a logo file first");
      return;
    }

    try {
      setLogoUploading(true);
      
      // Validate logo requirements (600x600px, JPG/PNG, under 1MB)
      const validationError = await validateLogoRequirements(logo);
      if (validationError) {
        showErrorToast(validationError);
        return;
      }

      const logoUrl = await uploadLogo(logo);
      
      if (!logoUrl) {
        throw new Error("Upload succeeded but no URL returned");
      }
      
      // Update business with logo URL
      const { error } = await (supabase as any)
        .from("businesses")
        .update({ logo_url: logoUrl })
        .eq("id", businessId);

      if (error) {
        throw new Error(handleError(error, "Failed to save logo URL to database"));
      }

      console.log('Setting logoUrl in hook to:', logoUrl);
      setLogoUrl(logoUrl);
      setLogo(null);
      
      // Trigger business data refresh to update logo URL
      if (onLogoUpdate) {
        console.log('Calling refresh callback to update business data');
        onLogoUpdate();
        console.log('Refresh callback completed');
      } else {
        console.warn('No refresh callback provided!');
      }
      
      // Show centered success message with more detailed feedback
      showSuccessToast("🎉 Business logo uploaded successfully! Your new logo is now visible on your dashboard.");
    } catch (error: any) {
      showErrorToast(handleError(error, "Failed to upload logo"));
    } finally {
      setLogoUploading(false);
    }
  };

  const uploadGalleryImages = async (files: File[]): Promise<string[]> => {
    const uploadPromises = files.map(async (file) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `gallery_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from("business-galleries")
        .upload(`galleries/${fileName}`, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        throw new Error(`Gallery upload failed: ${error.message}`);
      }
      
      if (!data?.path) {
        throw new Error("Upload succeeded but no path returned");
      }
      
      const { data: { publicUrl } } = supabase.storage
        .from("business-galleries")
        .getPublicUrl(data.path);
      
      return publicUrl || data.path;
    });

    return Promise.all(uploadPromises);
  };

  const handleGalleryUpload = async () => {
    if (newGalleryImages.length === 0) {
      showErrorToast("Please select images to upload");
      return;
    }
    
    if (!businessId) {
      showErrorToast("Business information not loaded");
      return;
    }

    try {
      setGalleryUploading(true);
      
      // Validate all files
      const allErrors: string[] = [];
      newGalleryImages.forEach((file, index) => {
        const errors = validateFileUpload(file, 5, ['image/jpeg', 'image/png', 'image/webp']);
        if (errors.length > 0) {
          allErrors.push(`File ${index + 1}: ${errors.join(', ')}`);
        }
      });
      
      if (allErrors.length > 0) {
        showErrorToast(`File validation failed: ${allErrors.join('; ')}`);
        return;
      }

      // Check total gallery limit
      const totalImages = galleryImages.length + newGalleryImages.length;
      if (totalImages > 10) {
        showErrorToast(`Maximum 10 gallery images allowed. You have ${galleryImages.length} and trying to add ${newGalleryImages.length}`);
        return;
      }

      const uploadedUrls = await uploadGalleryImages(newGalleryImages);
      
      if (!uploadedUrls || uploadedUrls.length === 0) {
        throw new Error("Upload succeeded but no URLs returned");
      }
      
      const updatedGallery = [...galleryImages, ...uploadedUrls];
      
      // Update business with new gallery
      const { error } = await (supabase as any)
        .from("businesses")
        .update({ gallery: updatedGallery })
        .eq("id", businessId);

      if (error) {
        throw new Error(handleError(error, "Failed to save gallery to database"));
      }

      setGalleryImages(updatedGallery);
      setNewGalleryImages([]);
      showSuccessToast(`${uploadedUrls.length} gallery images uploaded successfully!`);
    } catch (error: any) {
      showErrorToast(handleError(error, "Failed to upload gallery images"));
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleDeleteGalleryImage = async (imageUrl: string) => {
    if (!businessId) return;

    try {
      const updatedGallery = galleryImages.filter(url => url !== imageUrl);
      
      const { error } = await (supabase as any)
        .from("businesses")
        .update({ gallery: updatedGallery })
        .eq("id", businessId);

      if (error) throw error;

      setGalleryImages(updatedGallery);
      showSuccessToast("Image removed from gallery");
    } catch (error: any) {
      showErrorToast(error.message || "Failed to remove image");
    }
  };

  const handleReorderGallery = async (fromIndex: number, toIndex: number) => {
    if (!businessId) return;

    try {
      const newGallery = [...galleryImages];
      const [removed] = newGallery.splice(fromIndex, 1);
      newGallery.splice(toIndex, 0, removed);
      
      const { error } = await (supabase as any)
        .from("businesses")
        .update({ gallery: newGallery })
        .eq("id", businessId);

      if (error) throw error;

      setGalleryImages(newGallery);
      showSuccessToast("Gallery reordered successfully");
    } catch (error: any) {
      showErrorToast(error.message || "Failed to reorder gallery");
    }
  };

  // Video upload function
  const uploadShopVideo = async (file: File): Promise<string> => {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileName = `shop_video_${timestamp}_${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `shop-videos/${fileName}`;
    
    const { data, error } = await supabase.storage
      .from("business-videos")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type || 'video/mp4'
      });

    if (error) {
      throw new Error(`Video upload failed: ${error.message}`);
    }
    
    if (!data?.path) {
      throw new Error("Upload succeeded but no path returned");
    }
    
    const { data: { publicUrl } } = supabase.storage
      .from("business-videos")
      .getPublicUrl(data.path);
    
    return publicUrl;
  };

  const handleVideoUpload = async () => {
    if (!shopVideo || !businessId) {
      showErrorToast("Please select a video file first");
      console.error('Video upload error: Missing video or businessId', { shopVideo, businessId });
      return;
    }

    console.log('Starting video upload...', {
      fileName: shopVideo.name,
      fileSize: shopVideo.size,
      fileType: shopVideo.type,
      businessId
    });

    try {
      setVideoUploading(true);
      
      // Validate video format
      const validFormats = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
      if (!validFormats.includes(shopVideo.type)) {
        showErrorToast("Invalid video format. Please use MP4, WebM, OGG, or MOV");
        console.error('Invalid video format:', shopVideo.type);
        setVideoUploading(false);
        return;
      }
      
      // Validate file size (max 500MB)
      const maxSize = 500 * 1024 * 1024; // 500MB in bytes
      if (shopVideo.size > maxSize) {
        showErrorToast("Video file size exceeds 500MB limit");
        console.error('Video too large:', shopVideo.size);
        setVideoUploading(false);
        return;
      }

      console.log('Uploading video to storage...');
      const videoUrl = await uploadShopVideo(shopVideo);
      console.log('Video uploaded successfully:', videoUrl);
      
      if (!videoUrl) {
        throw new Error("Upload succeeded but no URL returned");
      }
      
      // Update business with shop video URL
      console.log('Updating database...', { businessId, videoUrl });
      const { data, error } = await (supabase as any)
        .from("businesses")
        .update({ shop_video_url: videoUrl })
        .eq("id", businessId)
        .select();

      console.log('Database update result:', { data, error });

      if (error) {
        console.error('Database update error:', error);
        throw new Error(handleError(error, "Failed to save video URL to database"));
      }

      console.log('Video URL saved to database successfully');
      setShopVideoUrl(videoUrl);
      setShopVideo(null);
      
      // Trigger business data refresh to update video URL
      if (onLogoUpdate) {
        console.log('Triggering business data refresh');
        onLogoUpdate();
      }
      
      showSuccessToast("🎥 Shop video uploaded successfully! Your video is now visible on your profile.");
    } catch (error: any) {
      console.error('Video upload error:', error);
      showErrorToast(handleError(error, "Failed to upload video"));
    } finally {
      setVideoUploading(false);
    }
  };

  const deleteVideoFromStorage = async (videoUrl: string) => {
    const marker = '/storage/v1/object/public/business-videos/';
    if (!videoUrl.includes(marker)) {
      return;
    }

    const path = decodeURIComponent(videoUrl.split(marker)[1] || '');
    if (!path) {
      return;
    }

    const { error } = await supabase.storage.from('business-videos').remove([path]);
    if (error) {
      console.warn('Could not remove previous video from storage:', error.message);
    }
  };

  const handleDeleteVideo = async () => {
    if (!businessId) {
      showErrorToast('Business information not loaded');
      return;
    }

    if (!shopVideoUrl) {
      showErrorToast('No shop video to delete');
      return;
    }

    try {
      setVideoDeleting(true);

      const previousUrl = shopVideoUrl;
      const { error } = await (supabase as any)
        .from('businesses')
        .update({ shop_video_url: null })
        .eq('id', businessId);

      if (error) {
        throw new Error(handleError(error, 'Failed to remove shop video'));
      }

      await deleteVideoFromStorage(previousUrl);
      setShopVideoUrl('');
      setShopVideo(null);

      if (onLogoUpdate) {
        onLogoUpdate();
      }

      showSuccessToast('Shop video deleted successfully');
    } catch (error: any) {
      showErrorToast(handleError(error, 'Failed to delete shop video'));
    } finally {
      setVideoDeleting(false);
    }
  };

  return {
    // Logo management
    logo,
    setLogo,
    logoUploading,
    logoUrl,
    setLogoUrl,
    handleLogoUpload,
    
    // Gallery management
    galleryImages,
    setGalleryImages,
    newGalleryImages,
    setNewGalleryImages,
    galleryUploading,
    handleGalleryUpload,
    handleDeleteGalleryImage,
    handleReorderGallery,
    
    // Video management
    shopVideo,
    setShopVideo,
    videoUploading,
    videoDeleting,
    shopVideoUrl,
    handleVideoUpload,
    handleDeleteVideo
  };
};